import {
	defaultCompanySettings,
	getCurrentCompanySettings,
} from '../../utils/companySettings';
import {
	selectAuthenticationContext,
	useSelectSettings,
} from '../../selectors';
import { setSettings as setSettingsAction } from '../../actions/app.actions';
import { updateWhatsNewState } from '../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { useGetCurrentApp } from '../../hooks/useGetCurrentApp';
import { useGlobalization } from '../GlobalizationProvider/useGlobalization';
import React, { useEffect } from 'react';
import type { CompanySettings, Settings } from 'types';
import UnitsProvider from '../../contexts/Settings/Units';

export const useSettings = () => {
	const dispatch = useDispatch();
	const _settings = useSelectSettings();

	const setSettings = React.useCallback(
		(settings: Omit<Settings, 'settingsLoaded'> | CompanySettings) => {
			// The old setSettings worked by sending CompanySettings so, some apps send settings in a different shape

			const s =
				'companySettings' in settings
					? settings
					: ({ companySettings: settings } as unknown as Settings);

			dispatch(setSettingsAction(s));
		},
		[],
	);

	return { ..._settings, setSettings };
};

interface SettingsProviderProps {
	children: React.ReactElement;
}

/**
 * Company Settings provider
 *
 * Falls back to US defaults
 */
export const SettingsProvider = ({
	children,
}: SettingsProviderProps): JSX.Element => {
	const dispatch = useDispatch();
	const { i18n } = useGlobalization();
	const { companySettings, settingsLoaded, setSettings } = useSettings();
	const { isInitializing, defaultSite } = useSelector(
		selectAuthenticationContext,
	);
	const shouldLoadSettings = !isInitializing && !settingsLoaded;

	const currentApp = useGetCurrentApp();

	useEffect(() => {
		if (shouldLoadSettings && defaultSite) {
			getCurrentCompanySettings(currentApp)
				.then(({ companySettings, lastWhatsNewVersionSeen }) => {
					setSettings({ companySettings });
					i18n.changeLanguage(companySettings.language);
					dispatch(
						updateWhatsNewState({
							lastWhatsNewVersionSeen,
							isLoading: false,
						}),
					);
				})
				.catch(() => {
					const newCompanySettings = {
						...defaultCompanySettings,
						organization: defaultSite,
					};
					setSettings({
						companySettings: newCompanySettings,
					});
				});
		}
	}, [shouldLoadSettings, defaultSite, currentApp]);

	return (
		<UnitsProvider system={companySettings.UOM}>{children}</UnitsProvider>
	);
};

export default SettingsProvider;
